import React from 'react';
import { Shield, Wifi, AlertTriangle, CheckCircle, Users, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCompanySecurityValidator } from '@/hooks/useCompanySecurityValidator';
import { useDataIntegrityValidator } from '@/hooks/useDataIntegrityValidator';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useRealtimeUsers } from '@/hooks/useRealtimeUsers';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

/**
 * Badge dinámico que muestra el estado real de seguridad del sistema
 * con colores y texto que cambian según la situación
 */
export const SecurityStatusBadge: React.FC = () => {
  const { user, selectedCompany } = useAuth();
  const { isValidating } = useCompanySecurityValidator();
  const { networkStatus, integrityChecks } = useDataIntegrityValidator();
  const { syncStatus } = useOfflineSync();
  const { stats } = useRealtimeUsers();

  if (!user || !selectedCompany) return null;

  // Determinar el estado general del sistema
  const hasErrors = syncStatus.syncErrors.length > 0 || 
                   integrityChecks.some(check => check.status === 'failure') ||
                   !networkStatus.isOnline;

  const hasCriticalErrors = syncStatus.syncErrors.length > 0 || !networkStatus.isOnline;
  
  // Función para obtener el estado y texto dinámico
  const getSystemStatus = () => {
    if (hasCriticalErrors) {
      return {
        status: 'critical',
        text: 'Sistema Inseguro',
        color: 'text-status-error',
        bgColor: 'bg-transparent hover:bg-status-error-light',
        borderColor: 'border-transparent',
        icon: AlertTriangle,
        description: 'Errores críticos detectados'
      };
    }
    
    if (hasErrors) {
      return {
        status: 'warning',
        text: 'Sistema Con Alertas',
        color: 'text-warning',
        bgColor: 'bg-transparent hover:bg-status-warning-light',
        borderColor: 'border-transparent',
        icon: Clock,
        description: 'Algunos problemas menores detectados'
      };
    }
    
    if (isValidating) {
      return {
        status: 'validating',
        text: 'Validando Seguridad',
        color: 'text-status-info',
        bgColor: 'bg-transparent hover:bg-status-info-light',
        borderColor: 'border-transparent',
        icon: Shield,
        description: 'Verificando integridad del sistema'
      };
    }

    return {
      status: 'secure',
      text: 'Sistema Seguro',
      color: 'text-status-success',
      bgColor: 'bg-transparent hover:bg-status-success-light',
      borderColor: 'border-transparent',
      icon: CheckCircle,
      description: 'Todos los sistemas funcionan correctamente'
    };
  };

  const systemStatus = getSystemStatus();
  const StatusIcon = systemStatus.icon;

  return (
    <TooltipProvider>
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "relative h-8 w-8 p-0 transition-all duration-200 rounded-full",
                  "flex items-center justify-center",
                  systemStatus.bgColor,
                  systemStatus.borderColor
                )}
              >
                {/* Icono dinámico con animación */}
                <div className="relative">
                  <StatusIcon className={cn("h-5 w-5", systemStatus.color)} />
                  {isValidating && (
                    <div className="absolute -top-1 -right-1">
                      <div className="w-2 h-2 bg-status-info rounded-full animate-pulse" />
                    </div>
                  )}
                  {hasCriticalErrors && (
                    <div className="absolute -top-1 -right-1">
                      <div className="w-2 h-2 bg-status-error rounded-full animate-bounce" />
                    </div>
                  )}
                </div>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          
          <TooltipContent side="bottom" className="max-w-xs">
            <div className="space-y-1">
              <p className="font-medium">{systemStatus.text}</p>
              <p className="text-xs text-muted-foreground">
                {systemStatus.description}
              </p>
              {stats.activeUsers > 0 && (
                <p className="text-xs text-muted-foreground">
                  {stats.activeUsers} usuarios conectados
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>

        <PopoverContent className="w-80 p-0" align="end" sideOffset={8}>
          <div className="p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <StatusIcon className={cn("h-5 w-5", systemStatus.color)} />
                <div>
                  <h3 className="font-semibold text-sm">{systemStatus.text}</h3>
                  <p className="text-xs text-muted-foreground">
                    {selectedCompany.name}
                  </p>
                </div>
              </div>
              <Badge 
                variant="outline" 
                className={cn("text-xs", systemStatus.bgColor, systemStatus.borderColor)}
              >
                {systemStatus.status === 'critical' ? 'Crítico' : 
                 systemStatus.status === 'warning' ? 'Alerta' :
                 systemStatus.status === 'validating' ? 'Validando' : 'Seguro'}
              </Badge>
            </div>

            {/* Estado de Conectividad */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Wifi className={cn(
                    "h-4 w-4",
                    networkStatus.isOnline ? 'text-status-success' : 'text-status-error'
                  )} />
                  <span className="text-sm">Conectividad</span>
                </div>
                <Badge 
                  variant="outline" 
                  className={cn(
                    "text-xs",
                    networkStatus.isOnline ? 'bg-status-success-bg text-status-success-foreground' : 'bg-status-error-bg text-status-error-foreground'
                  )}
                >
                  {networkStatus.isOnline ? 'En línea' : 'Sin conexión'}
                </Badge>
              </div>
            </div>

            {/* Usuarios Activos */}
            {stats.activeUsers > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-status-info" />
                  <span className="text-sm">Usuarios activos</span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {stats.activeUsers}
                </Badge>
              </div>
            )}

            {/* Integridad de Datos */}
            {integrityChecks.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-status-success" />
                  <span className="text-sm">Integridad de Datos</span>
                </div>
                <div className="space-y-1">
                  {integrityChecks.slice(-2).map((check, index) => (
                    <div key={index} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{check.table}</span>
                      <Badge 
                        variant={check.status === 'success' ? 'default' : 'destructive'}
                        className="text-xs"
                      >
                        {check.status === 'success' ? 'OK' : 'Error'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sincronización Pendiente */}
            {syncStatus.pendingCount > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-amber-500" />
                  <span className="text-sm">Sincronización pendiente</span>
                </div>
                <Badge variant="secondary" className="text-xs bg-amber-50 text-amber-700">
                  {syncStatus.pendingCount}
                </Badge>
              </div>
            )}

            {/* Errores Críticos */}
            {syncStatus.syncErrors.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="h-4 w-4 text-status-error" />
                  <span className="text-sm text-status-error-foreground font-medium">Errores Críticos</span>
                </div>
                {syncStatus.syncErrors.slice(0, 2).map((error, index) => (
                  <p key={index} className="text-xs text-status-error-foreground truncate bg-status-error-light p-2 rounded">
                    {error}
                  </p>
                ))}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
};